import api from './api';
import { User } from '../types';

interface LoginResponse {
  user: User;
  token: string;
}

export interface VerificationRequired {
  requiresVerification: true;
  verificationId: string;
  message: string;
}

export interface EmailVerificationRequired {
  requiresEmailVerification: true;
  email: string;
  message: string;
}

export interface AccountBlocked {
  isBlocked: true;
  message: string;
}

export type LoginResult = LoginResponse | VerificationRequired | EmailVerificationRequired | AccountBlocked;

export interface RegisterResult {
  requiresEmailVerification: true;
  email: string;
  message: string;
}

interface VerifyCodeResponse {
  user: User;
  token: string;
  deviceToken: string;
}

interface VerifyEmailResponse {
  user: User;
  token: string;
  deviceToken: string;
  message: string;
}

export const authService = {
  async login(email: string, password: string): Promise<LoginResult> {
    const deviceToken = localStorage.getItem('deviceToken');
    const response = await api.post('/auth/login', { email, password }, {
      headers: deviceToken ? { 'x-device-token': deviceToken } : {}
    });
    return response.data;
  },

  async verifyCode(verificationId: string, code: string): Promise<VerifyCodeResponse> {
    const response = await api.post('/auth/verify-code', { verificationId, code });
    return response.data;
  },

  async register(name: string, email: string, phone: string, password: string): Promise<RegisterResult> {
    const response = await api.post('/auth/register', { name, email, phone, password });
    return response.data;
  },

  async verifyEmail(token: string): Promise<VerifyEmailResponse> {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  async resendVerification(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    const response = await api.get('/auth/check-email', { params: { email } });
    return response.data;
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },

  async getMe(): Promise<User> {
    const response = await api.get('/auth/me');
    return response.data;
  },
};
